/*
 * Starter scaffolds — Electron runtime port.
 *
 * The canonical implementation lives in lib/templates/starters.ts and
 * is the source of truth (tested via Vitest). This file is a plain-JS
 * port consumed by the main process at runtime. A vitest parity test
 * (lib/templates/__tests__/parity.test.ts) compares the two outputs to
 * keep them in sync.
 *
 * If you change a scaffold here, change the .ts version too — and run
 * the parity test before committing.
 */

export const VALID_FORMATS = new Set([
  'blank',
  'feature',
  'teleplay-single-cam',
  'sitcom-multi-cam',
  'stageplay',
  'vertical-short-series',
]);

function titleBlock(fields, format, extras = {}) {
  const credit = fields.credit != null ? fields.credit : 'Written by';
  const lines = [
    `Title: ${fields.title || 'Untitled'}`,
    `Credit: ${credit}`,
    `Author: ${fields.author || 'Anonymous'}`,
    `Format: ${format}`,
  ];
  for (const [key, value] of Object.entries(extras)) {
    if (value) lines.push(`${key}: ${value}`);
  }
  return lines.join('\n') + '\n\n';
}

function featureScaffold(fields) {
  return (
    titleBlock(fields, 'feature') +
    `FADE IN:

INT. COFFEE SHOP - MORNING

Steam rises from a row of cups along the counter. The bell over the door rings. A barista calls out an order no one claims.

MAYA, 30s, hopeful and under-caffeinated, scans the room for an open table.

MAYA
(to herself)
Just one free seat. Anywhere.

She spots one in the back corner. As she crosses the room a STRANGER beats her to it by half a step.

STRANGER
Sorry — were you heading for this one?

MAYA
No. Go ahead.

She turns to leave, then stops.

MAYA (CONT'D)
Actually. Would you mind sharing?

The stranger considers. Then gestures to the empty chair opposite.

STRANGER
Be my guest.

She sits. The morning rush rolls on without them.

CUT TO:

INT. COFFEE SHOP - LATER

Two empty cups between them. They are deep in conversation, leaning in.

FADE OUT.
`
  );
}

function teleplayScaffold(fields) {
  return (
    titleBlock(fields, 'teleplay-single-cam', {
      Series: fields.series || '',
      Episode: fields.episode || '',
    }) +
    `TEASER

FADE IN:

EXT. CITY ROOFTOP - NIGHT

Wind. The skyline glitters below. DETECTIVE REYES, 40s, stands at the edge holding a phone to her ear. Her breath fogs.

REYES
I'm on the roof. There's nobody here.

A pause. She lowers the phone slowly.

REYES (CONT'D)
Wait.

She turns, hand drifting toward her holster.

SMASH CUT TO BLACK.

> END OF TEASER <

> ACT ONE <

INT. PRECINCT BULLPEN - MORNING

Fluorescents hum. Detectives drift between desks with coffee. REYES, hair still wet, walks in late and ignores the looks.

CAPTAIN HOLLAND, 50s, leans in her doorway.

HOLLAND
You filed nothing about last night.

REYES
There was nothing to file.

HOLLAND
That's a choice. Make it the right one.

He walks off. Reyes drops into her chair, opens a drawer, takes out a small evidence bag. Inside: a single brass key.

> END OF ACT ONE <

> ACT TWO <

INT. REYES' APARTMENT - NIGHT

She turns the brass key in her palm under a desk lamp. Her phone buzzes. UNKNOWN NUMBER.

She answers.

REYES
Who is this?

(silence on the line)

REYES (CONT'D)
I said. Who is this.

The line goes dead.
`
  );
}

function sitcomScaffold(fields) {
  return (
    titleBlock(fields, 'sitcom-multi-cam', {
      Series: fields.series || '',
      Episode: fields.episode || '',
    }) +
    `> COLD OPEN <

INT. APARTMENT - LIVING ROOM - NIGHT

DALE, 30s, is on the couch eating cereal from a saucepan. JEN, 30s, his roommate, walks in holding a single houseplant.

JEN
We agreed. No more plants.

DALE
That's not a plant. That's a friend.

JEN
It's a plant.

DALE
It's a friend who happens to photosynthesize.

She stares at him. He stares back. The plant tilts slightly.

JEN
(to the plant)
I'm so sorry.

> END OF COLD OPEN <

> ACT ONE <

INT. APARTMENT - KITCHEN - MORNING

Jen pours coffee. Dale enters carrying THREE more plants.

JEN
Dale.

DALE
I can explain.

JEN
You have three new ones.

DALE
They were on sale.

JEN
For free?

DALE
Better than free. They paid me.

Jen sets the coffee pot down very, very carefully.

JEN
Why did the plants pay you, Dale.

DALE
(thinking)
That is a great question.

> END OF ACT ONE <

> ACT TWO <

INT. APARTMENT - LIVING ROOM - LATER

The room is now a jungle. Jen sits in the middle of it, surrounded. Dale brings her a cup of tea.

DALE
So. About the plants.

JEN
Mm-hm.

DALE
What if — and hear me out — we open a store.
`
  );
}

function stageplayScaffold(fields) {
  return (
    titleBlock(fields, 'stageplay') +
    `> SCENE 1 <

(A library. Late afternoon. Sunlight slants through tall windows onto a long oak table. MARGARET, 60s, sits alone, surrounded by open books and three unfinished cups of tea. The clock on the wall reads 4:47.)

(The door opens. THOMAS, 30s, enters cautiously. He holds a single envelope at his side.)

THOMAS
You said four-thirty.

MARGARET
I said before five.

THOMAS
That is not what you said.

(Beat. He sets the envelope on the table between them.)

MARGARET
Sit down, Thomas.

(He sits. Margaret does not look at the envelope.)

MARGARET (CONT'D)
Did you read it?

THOMAS
No.

MARGARET
You're lying.

THOMAS
(quietly)
Yes.

(She closes the book in front of her. The sound is louder than it should be.)
`
  );
}

function blankScaffold(fields) {
  // Title page only — the editor seeds an empty Action paragraph on
  // mount when the Fountain body is empty. Keeps the JS runtime byte-
  // identical to the canonical TS scaffold in lib/templates/starters.ts.
  return titleBlock(fields, 'blank');
}

// Vertical Short Series scaffold — see lib/templates/starters.ts for
// the canonical authored source and the design rationale. This must
// stay byte-identical to that file's VERTICAL_SHORT_SERIES_SCAFFOLD;
// the parity test enforces it.
function verticalShortSeriesScaffold(fields) {
  return (
    titleBlock(fields, 'vertical-short-series', {
      Series: fields.series || '',
      Episode: fields.episode || '1',
      'Episodes Total': '60',
      'Paywall Episode': '10',
    }) +
    `# SERIES BIBLE

[[This bible isn't a deliverable — it's your safety rail. Fill it before you script Ep 1. Delete the bracketed coaching as you go; it doesn't print to PDF.]]

## Premise

A [protagonist] who [wants X] but [structural obstacle Y].

[[One sentence. Verticals run on STRUCTURAL conflict — built into the setup, not invented scene by scene. Working premises in 2026: contract marriage, secret heiress returns, exiled CEO, fated mate, revenge wife, swap brides, hidden identity.]]

## Two Leads

PROTAGONIST — [Name, age]. Wants: [one defining want].

ANTAGONIST / LOVE INTEREST — [Name, age]. Wants: [a want that collides with the protagonist's].

[[Verticals love faces in frame. Keep your ensemble to two leads who carry every episode. Side characters serve the central two — they don't compete for screen time.]]

## The Spine

By the final episode, [destination — one sentence].

[[Nail the destination BEFORE plotting episodes. Everything serves the destination. If a subplot doesn't bend toward the spine, cut it.]]

## Paywall Hook — Episode 10

At the end of Ep 10, [reveal or reversal that earns 30 cents from a stranger].

[[Episodes 1–10 are free on most platforms. Ep 10's closing beat decides whether the viewer pays. Plant the seed in Ep 1; pay it off here. Common shapes: secret identity revealed, betrayal exposed, forbidden kiss interrupted, "you're not who you said you are."]]

## Arc Map

Eps 1–10  — Premise + two or three emotional peaks. Paywall hits at Ep 10.

Eps 11–40 — Escalate. New pressure. Subplots introduce.

Eps 41–70 — Dark middle. Worst case plays out.

Eps 71–end — Resolve. Subplots resolve before the central arc. Earn the payoff.

===

# EPISODE 1

[[Each episode is 60–90 seconds — roughly one to two script pages. The Beat Engine below is the skeleton: write the timestamp landmarks FIRST, fill in dialogue second.]]

## HOOK — 0:00 to 0:15

[[Drop us mid-conflict. The first five seconds decide whether the viewer swipes. No slow setup. No introducing every character at once. Open on the most cinematic moment of the episode — even of the season.]]

INT. WEDDING HALL - DAY

A BRIDE in white. Her brand-new GROOM at the altar. The pastor is mid-sentence. The groom leans in and mouths a name — NOT HERS.

GROOM
(under his breath)
Don't say a word. Just smile.

## FRICTION — 0:15 to 1:00

[[Filmable conflict in the same physical space. Two people. Verbal or physical. Not emotional subtext — conflict you can SHOOT.]]

The BRIDE turns to face the GROOM. The pastor keeps going, oblivious.

BRIDE
(smiling for the crowd)
What name.

GROOM
(smiling back)
Sign the papers. We talk after.

BRIDE
We talk now.

## SPIKE — 1:00 to 1:30

[[A revelation or perspective shift that recontextualises the hook. The viewer should think: "Wait — so THAT'S what's going on."]]

The MAID OF HONOR steps forward to hand over the rings. Her eyes lock with the GROOM's. A private, devastating look.

BRIDE (V.O.)
I knew the wedding was a contract.
I didn't know it was for HER.

## BUTTON — final 0:05 to 0:10

[[Cut on the QUESTION, not the answer. End two seconds before it feels safe. Cliffhanger menu below — pick ONE, end hard.]]

PASTOR
Do you take this man—

The BRIDE turns to face the crowd. Lifts her chin.

BRIDE
No.

SMASH CUT TO BLACK.

===

# CLIFFHANGER MENU

[[Pick a different type for each episode. Don't repeat in consecutive episodes — viewers spot patterns and bounce.]]

REVELATION — a secret breaks the surface.

CONFRONTATION — two people meet who shouldn't.

DEADLINE — a clock just started.

INTRUSION — someone walks in mid-scene.

REVERSAL — what we believed was wrong.

THREAT — violence about to land.

BETRAYAL — an ally is the enemy.

IDENTITY — someone is not who they claimed.

===

# COMMON MISTAKES TO AVOID

[[Verticals fail when writers treat them like horizontal TV scaled down.]]

Slow openings. Start mid-conflict. No establishing scenes.

Exposition dialogue. No "as you know, Marcus, your father…" Cut explanations; keep evidence.

Action sequences. Hard to read in 9:16. Replace with faces and reactions.

Ensemble casts. Keep the core to two leads.

Resolved endings. Every episode pulls forward — even mid-arc beats end on a question.

Small talk. Every line either advances conflict or reveals character under pressure.

[[Delete this section and the Cliffhanger Menu once you don't need them. Their presence is a TODO for you, not a feature for the reader.]]
`
  );
}

const SCAFFOLDS = {
  blank: blankScaffold,
  feature: featureScaffold,
  'teleplay-single-cam': teleplayScaffold,
  'sitcom-multi-cam': sitcomScaffold,
  stageplay: stageplayScaffold,
  'vertical-short-series': verticalShortSeriesScaffold,
};

export function createProjectFromTemplate(format, fields) {
  if (!VALID_FORMATS.has(format)) {
    throw new Error(`No scaffold for format: ${format}`);
  }
  const fountain = SCAFFOLDS[format](fields || {});
  return { fountain, format };
}

export function listFormats() {
  return [
    {
      id: 'blank',
      displayName: 'Blank',
      description:
        'Empty starter. Title page plus a fresh page to begin writing — open on BLACK., FADE IN:, a slugline, or anything else.',
    },
    {
      id: 'vertical-short-series',
      displayName: 'Vertical Short Series',
      description:
        '60–90s episodes · 60–100 ep series · ReelShort / DramaBox. Self-guiding template with a Series Bible, Beat Engine (Hook / Friction / Spike / Button) and a cliffhanger menu.',
      // Surfaced in the New-Project modal as a "New" pill in the card
      // header. The renderer dismisses the pill once the writer opens
      // (selects) the card for the first time.
      isNew: true,
    },
    {
      id: 'feature',
      displayName: 'Feature Screenplay',
      description: 'Industry-standard US feature film format.',
    },
    {
      id: 'teleplay-single-cam',
      displayName: 'Hour-Long Single-Cam Teleplay',
      description: 'One-hour single-camera television format with act breaks.',
    },
    {
      id: 'sitcom-multi-cam',
      displayName: 'Half-Hour Multi-Cam Sitcom',
      description:
        'Half-hour multi-camera sitcom format with double-spaced dialogue and mandatory act breaks.',
    },
    {
      id: 'stageplay',
      displayName: 'Stageplay',
      description: 'Modern stageplay format. Character names centered above dialogue.',
    },
  ];
}

// Backwards-compat alias for the older bridge/import.js callers.
export const WEB_FORMATS = listFormats();
